import React, { useMemo } from 'react';
import { Alert, Col, Row, Spinner } from 'react-bootstrap';
import { AnalyticsPeriod, MonitoringAnalyticsResponse } from './types';
import {
  buildVisibleInflowSeries,
  formatRangeLabel,
  getSourceLabel,
} from './utils';
import { AnalyticsControls, InflowChart, SummaryTile } from './Shared';

const getAnalyticsErrorMessage = (error: any, fallback: string) =>
  String(error?.data?.error || error?.data?.message || error?.message || fallback);

export default function InflowTab({
  analytics,
  isLoading,
  isFetching,
  error,
  period,
  startDate,
  endDate,
  selectedSource,
  sources,
  onPeriodChange,
  onStartDateChange,
  onEndDateChange,
  onSourceChange,
}: {
  analytics?: MonitoringAnalyticsResponse;
  isLoading: boolean;
  isFetching: boolean;
  error: any;
  period: AnalyticsPeriod;
  startDate: string;
  endDate: string;
  selectedSource: string;
  sources: string[];
  onPeriodChange: (value: AnalyticsPeriod) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onSourceChange: (value: string) => void;
}) {
  const visibleSeries = useMemo(
    () => buildVisibleInflowSeries(analytics, selectedSource),
    [analytics, selectedSource],
  );

  const inflowData = useMemo(() => {
    const series = visibleSeries[0];
    if (!series) return [];

    const average =
      series.values.reduce((sum, item) => sum + item.count, 0) /
      Math.max(series.values.length, 1);

    return series.values.map(item => ({
      bucket: item.bucket,
      count: item.count,
      average,
    }));
  }, [visibleSeries]);

  const average = inflowData[0]?.average || 0;

  return (
    <>
      <AnalyticsControls
        period={period}
        startDate={startDate}
        endDate={endDate}
        sources={sources}
        selectedSource={selectedSource}
        onPeriodChange={onPeriodChange}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onSourceChange={onSourceChange}
      />

      {isLoading ? (
        <div className="adsure-loading-state">
          <Spinner animation="border" className="me-2" />
          Loading incoming test analytics...
        </div>
      ) : error ? (
        <Alert variant="danger">
          {getAnalyticsErrorMessage(
            error,
            'Failed to load incoming test analytics.',
          )}
        </Alert>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <SummaryTile
                label="Selected Range"
                value={formatRangeLabel(analytics?.range)}
                tone="#165177"
              />
            </Col>
            <Col md={4}>
              <SummaryTile
                label="Source"
                value={
                  selectedSource === 'all'
                    ? 'All Sources'
                    : getSourceLabel(selectedSource || 'NA')
                }
                tone="#198754"
              />
            </Col>
            <Col md={4}>
              <SummaryTile
                label="Incoming Tests"
                value={inflowData.reduce((sum, item) => sum + item.count, 0)}
                tone="#a50135"
              />
            </Col>
          </Row>

          <InflowChart
            title="Incoming Tests Trend"
            subtitle={`${isFetching ? 'Refreshing live data. ' : ''}Shows intake trend for the selected source and overlays the average line for the chosen period.`}
            data={inflowData}
            average={average}
            color="#165177"
          />
        </>
      )}
    </>
  );
}
